import React, { useEffect, useMemo, useState } from "react";
import "../fonts/breuer-headline.css";
import "./thankyou.css";
import { useDispatch, useSelector } from "react-redux";
import { selectAdminToken } from "../sessionSlice";
import { setBackButtonUrl } from "../uiSlice";

import { Parchment, useSceneBackground } from "../escapeShared";
import thankYouBg from "../img/bg6.jpg";
import thankYouBgMobile from "../img/mob_bg4.jpg";
// 3D pirate emoji art: Microsoft Fluent Emoji (MIT licence)
import gemImg from "./img/gem.png";
import crownImg from "./img/crown.png";
import parrotImg from "./img/parrot.png";
import anchorImg from "./img/anchor.png";
import pirateFlagImg from "./img/pirate-flag.png";

const FEEDBACK_OPTIONS = [
  { value: "Amazingggg", emoji: gemImg, label: "Amazingggg" },
  { value: "Loved it", emoji: crownImg, label: "Loved it" },
  { value: "It was fun", emoji: parrotImg, label: "It was fun" },
  { value: "It was okay", emoji: anchorImg, label: "It was okay" },
];

// game.jsx's 20-minute room limit; a timed-out run is saved as exactly this.
const GAME_TIME_LIMIT_SEC = 20 * 60;

/** "MM:SS" or "HH:MM:SS" -> seconds (null if unparseable). */
function clockToSeconds(clock) {
  const parts = String(clock ?? "").trim().split(":").map((p) => parseInt(p, 10));
  if (!parts.length || parts.some((n) => !Number.isFinite(n))) return null;
  return parts.reduce((acc, n) => acc * 60 + n, 0);
}

const CONFETTI_MS = 2000;
const CONFETTI_COUNT = 64;

function buildEmojiConfettiPieces() {
  const ts = Date.now();
  return Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
    key: `${ts}-${i}`,
    x: Math.random() * 92 + 4,
    y: Math.random() * 85 + 5,
    dx: (Math.random() - 0.5) * 520,
    dy: (Math.random() - 0.5) * 600 + Math.random() * 120,
    rot: Math.random() * 1080 - 540,
    delay: Math.random() * 0.45,
    size: 36 + Math.floor(Math.random() * 44),
  }));
}

function readStoredUser(user) {
  try {
    const fromSession = JSON.parse(sessionStorage.getItem("userData") || "{}");
    if (
      fromSession &&
      typeof fromSession === "object" &&
      (fromSession.userId || fromSession.userid || fromSession.id)
    ) {
      return fromSession;
    }
  } catch {
    /* ignore */
  }
  return user && typeof user === "object" ? user : {};
}

function readThankYouUserId(stored) {
  const id = stored?.userId ?? stored?.userid ?? stored?.id;
  return id != null && String(id).trim() !== "" ? String(id).trim() : "";
}

export default function ThankYou() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  useSceneBackground(thankYouBg, thankYouBgMobile);

  const backendBase = useMemo(
    () => String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""),
    []
  );

  const storedUser = useMemo(() => readStoredUser(user), [user]);
  const viewerId = useMemo(
    () => readThankYouUserId(storedUser),
    [storedUser]
  );
  const sessionId = storedUser?.sessionId;
  const organizationId = storedUser?.organizationId;

  const [reportTime, setReportTime] = useState(null);
  const [savedFeedback, setSavedFeedback] = useState("");
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const [confettiOn, setConfettiOn] = useState(false);
  const [confettiEmoji, setConfettiEmoji] = useState("");
  const [confettiPieces, setConfettiPieces] = useState(null);

  useEffect(() => {
    if (user?.backButtonRedirect != null) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [user, dispatch]);

  // Feedback can run taller than the screen on phones.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = { html: html.style.overflow, body: body.style.overflow };
    body.classList.add("thank-you-scroll");
    html.style.overflow = "auto";
    body.style.overflow = "auto";
    return () => {
      body.classList.remove("thank-you-scroll");
      html.style.overflow = prev.html;
      body.style.overflow = prev.body;
    };
  }, []);

  useEffect(() => {
    if (!viewerId || !adminToken || !backendBase || !sessionId || !organizationId) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${backendBase}/fetchReport`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ userId: viewerId, sessionId, organizationId }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled || !res.ok || !data.success || !data.report) return;
        const r = data.report;
        if (r.feedback != null && String(r.feedback).trim() !== "") {
          setSavedFeedback(String(r.feedback).trim());
        }
        if (r.time != null && String(r.time).trim() !== "") {
          setReportTime(String(r.time).trim());
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewerId, adminToken, backendBase, sessionId, organizationId]);

  const submitFeedback = async (value) => {
    if (!viewerId || !adminToken || !backendBase || feedbackSaving || savedFeedback) {
      return;
    }
    setFeedbackError("");
    setFeedbackSaving(true);
    try {
      const res = await fetch(`${backendBase}/welcomeStageFeedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: viewerId,
          sessionId,
          organizationId,
          feedback: value,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setFeedbackError(data.message || "Could not save feedback. Please try again.");
        return;
      }
      setSavedFeedback(value);

      const opt = FEEDBACK_OPTIONS.find((o) => o.value === value);
      const emojiChar = opt?.emoji || "";
      const reduceMotion =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (emojiChar && !reduceMotion) {
        setConfettiEmoji(emojiChar);
        setConfettiPieces(buildEmojiConfettiPieces());
        setConfettiOn(true);
        window.setTimeout(() => {
          setConfettiOn(false);
          setConfettiPieces(null);
          setConfettiEmoji("");
        }, CONFETTI_MS);
      }
    } catch (e) {
      setFeedbackError(e.message || "Could not save feedback.");
    } finally {
      setFeedbackSaving(false);
    }
  };

  const feedbackLocked = Boolean(savedFeedback) || feedbackSaving;
  const canSubmitFeedback = Boolean(viewerId && adminToken && backendBase);


  // Ran out the clock instead of escaping (>= also covers older records
  // saved with time past 20:00 before the timer was capped).
  const reportSec = clockToSeconds(reportTime);
  const timedOut = reportSec != null && reportSec >= GAME_TIME_LIMIT_SEC;

  return (
    <>
      {confettiOn && confettiEmoji && confettiPieces ? (
        <div className="thank-you-confetti" aria-hidden="true">
          {confettiPieces.map((p) => (
            <span
              key={p.key}
              className="thank-you-confetti-emoji"
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                fontSize: p.size,
                "--ty-dx": `${p.dx}px`,
                "--ty-dy": `${p.dy}px`,
                "--ty-rot": `${p.rot}deg`,
                animationDelay: `${p.delay}s`,
              }}
            >
              <img src={confettiEmoji} alt="" />
            </span>
          ))}
        </div>
      ) : null}

      <div className="thank-you-page">
        <Parchment className="thank-you-card">
        <h1 className="thank-you-title">Thank You!</h1>

        {reportTime != null ? (
          timedOut ? (
            <p className="thank-you-congrats">
              Time&apos;s up! You couldn&apos;t escape within 20 minutes this time, but thank
              you for playing.
            </p>
          ) : (
            <p className="thank-you-congrats">
              Congratulations! You have escaped with the Queen&apos;s Jewels in {reportTime} mins
            </p>
          )
        ) : null}

        <section
          className="thank-you-feedback"
          aria-label="How was your experience? Tap an emoji to share your thoughts"
        >
          <h2 className="thank-you-feedback__title">
            How was your experience? Tap an emoji to share your thoughts{" "}
            <img src={pirateFlagImg} className="thank-you-feedback__title-icon" alt="" />
          </h2>
          <div className="thank-you-feedback__grid">
            {FEEDBACK_OPTIONS.map((opt) => {
              const selected = savedFeedback === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`thank-you-feedback__card${
                    selected ? " thank-you-feedback__card--selected" : ""
                  }`}
                  onClick={() => submitFeedback(opt.value)}
                  disabled={!canSubmitFeedback || feedbackLocked}
                  aria-pressed={selected}
                >
                  <span className="thank-you-feedback__emoji" aria-hidden="true">
                    <img src={opt.emoji} alt="" />
                  </span>
                  <span className="thank-you-feedback__label">{opt.label}</span>
                </button>
              );
            })}
          </div>
          {feedbackError ? (
            <p className="thank-you-feedback__error">{feedbackError}</p>
          ) : null}
          <p className="thank-you-feedback__hint">Your feedback helps us improve!</p>

        </section>
        </Parchment>
      </div>
    </>
  );
}
